import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ThermostatAuto as ThermostatIcon,
  Opacity as ChlorineIcon,
  Science as PhIcon,
  ToggleOn as ToggleOnIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { PoolSettings, PoolSettingsUpdate } from '../../types';

interface AutoControlSettingsFormProps {
  settings: PoolSettings['auto_control'];
  onChange: (data: PoolSettingsUpdate['auto_control']) => void;
}

const AutoControlSettingsForm: React.FC<AutoControlSettingsFormProps> = ({
  settings,
  onChange,
}) => {
  const [formData, setFormData] = useState({
    enabled: settings.enabled,
    temperature_control: settings.temperature_control,
    chlorine_control: settings.chlorine_control,
    ph_control: settings.ph_control,
  });

  // settings prop이 변경되면 formData 업데이트
  useEffect(() => {
    setFormData({
      enabled: settings.enabled,
      temperature_control: settings.temperature_control,
      chlorine_control: settings.chlorine_control,
      ph_control: settings.ph_control,
    });
  }, [settings]);

  // 스위치 값 변경 핸들러
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    
    let newFormData = {
      ...formData,
      [name]: checked,
    };

    // 자동 제어가 비활성화되면 모든 개별 제어도 비활성화
    if (name === 'enabled' && !checked) {
      newFormData = {
        ...newFormData,
        temperature_control: false,
        chlorine_control: false,
        ph_control: false,
      };
    }

    setFormData(newFormData);
    onChange(newFormData);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        자동 제어 설정
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        시설 환경을 자동으로 유지하기 위한 제어 시스템을 설정. 자동 제어 시스템은 수질과 온도를 설정한 범위 내로 유지.
      </Typography>

      {/* 메인 활성화 스위치 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ToggleOnIcon fontSize="large" color={formData.enabled ? "primary" : "disabled"} />
                <Box>
                  <Typography variant="h6">자동 제어 시스템</Typography>
                  <Typography variant="body2" color="text.secondary">
                    센서 데이터에 기반한 자동 환경 제어 기능을 활성화/비활성화
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={handleSwitchChange}
                    name="enabled"
                    color="primary"
                    size="medium"
                  />
                }
                label={formData.enabled ? "활성화됨" : "비활성화됨"}
                labelPlacement="start"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!formData.enabled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          자동 제어 시스템이 비활성화됨. 개별 제어 설정을 사용하려면 먼저 자동 제어 시스템을 활성화.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 수온 자동 제어 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="수온 자동 제어" 
              subheader="온도를 설정 범위 내로 유지"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              avatar={<ThermostatIcon color={formData.enabled && formData.temperature_control ? "primary" : "disabled"} />}
              action={
                <Tooltip title="설정된 온도 범위를 벗어날 경우 자동으로 히터 또는 냉각 시스템을 가동.">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.temperature_control}
                    onChange={handleSwitchChange}
                    name="temperature_control"
                    color="primary"
                    disabled={!formData.enabled}
                  />
                }
                label={formData.temperature_control ? "활성화됨" : "비활성화됨"}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                수온 자동 제어는 히터와 냉각 시스템을 제어하여 시설 물의 온도를 설정된 범위 내로 유지.
              </Typography>
              {formData.enabled && formData.temperature_control && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  수온 설정 범위를 벗어나면 자동으로 조절됨
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 염소 자동 제어 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="염소량 자동 제어" 
              subheader="소독 약품 농도 자동 유지"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              avatar={<ChlorineIcon color={formData.enabled && formData.chlorine_control ? "primary" : "disabled"} />}
              action={
                <Tooltip title="설정된 염소 농도 범위를 벗어날 경우 자동으로 염소 투입 시스템을 가동.">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.chlorine_control}
                    onChange={handleSwitchChange}
                    name="chlorine_control"
                    color="primary"
                    disabled={!formData.enabled}
                  />
                }
                label={formData.chlorine_control ? "활성화됨" : "비활성화됨"}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                염소 자동 제어는 소독 약품 투입 시스템을 제어하여 적정 염소 농도를 유지.
              </Typography>
              {formData.enabled && formData.chlorine_control && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  염소 농도가 설정 범위를 벗어나면 자동으로 조절됨
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* pH 자동 제어 */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader 
              title="pH 자동 제어" 
              subheader="수소이온농도 자동 유지"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{ variant: 'body2' }}
              avatar={<PhIcon color={formData.enabled && formData.ph_control ? "primary" : "disabled"} />}
              action={
                <Tooltip title="설정된 pH 범위를 벗어날 경우 자동으로 산성/알칼리성 약품 투입 시스템을 가동.">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ph_control}
                    onChange={handleSwitchChange}
                    name="ph_control"
                    color="primary"
                    disabled={!formData.enabled}
                  />
                }
                label={formData.ph_control ? "활성화됨" : "비활성화됨"}
              />
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                pH 자동 제어는 산성/알칼리성 약품 투입 시스템을 제어하여 적정 pH를 유지.
              </Typography>
              {formData.enabled && formData.ph_control && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  pH가 설정 범위를 벗어나면 자동으로 조절됨
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Alert severity="warning">
          <Typography variant="subtitle2">주의사항</Typography>
          <Typography variant="body2">
            자동 제어 시스템은 센서 데이터에 의존. 센서 고장 또는 오작동 시 수동 점검 필요.
            정기적인 시스템 점검을 통해 자동 제어 시스템의 안정적인 작동을 확인.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default AutoControlSettingsForm; 